import { CartQueryResult } from '@graphql/queries/__generated__/cart.generated'
import { MeQueryResult } from '@graphql/queries/__generated__/me.generated'
import { RewardProgramsQuery } from '@graphql/queries/__generated__/reward-programs.generated'
import { Divider } from '@mui/material'
import Button from '@mui/material/Button'
import PointsSvg from 'images/Points.svg'
import React, { useMemo } from 'react'
import Loader from '@components/Loader'
import { Link } from 'react-router-dom'
import { ACCOUNT_CREATE_URL, CHECKOUT_URL } from '../../../urls'
import { AfterPayInfo } from '@components/AfterPayInfo'
import FormattedPrice from '@components/FormattedPrice'
import { Trans, useTranslation } from 'react-i18next'
import FormattedDate from '@components/FormattedDate'
import CartShippingDiscount from './CartShippingDiscount'
import { CurrentCurrencyExchangeRateQueryResult } from '@graphql/queries/__generated__/shipping.generated'

interface CartLinesTotalProps {
  handleSignIn(): void

  isAuthenticated: boolean
  loading: boolean

  cartQueryResult: CartQueryResult
  meQueryResult: MeQueryResult
  firstRewardProgram: RewardProgramsQuery['rewardPrograms']['edges'][0] | null
  currencyExchangeRateQueryResult: CurrentCurrencyExchangeRateQueryResult
}

const CartLinesTotal: React.FC<CartLinesTotalProps> = ({
  handleSignIn,
  isAuthenticated,
  cartQueryResult,
  loading,
  meQueryResult,
  firstRewardProgram,
  currencyExchangeRateQueryResult,
}) => {
  const { t } = useTranslation()
  const itemSubtotal = cartQueryResult.data?.cart.totalPrice.discount.gross
  const rewards = meQueryResult.data?.me?.totalBalance?.gross || 0
  const creditsApplied = rewards > itemSubtotal ? itemSubtotal : rewards
  const earnPoints = useMemo(
    () =>
      cartQueryResult.data?.cart
        ? Math.round((cartQueryResult.data?.cart?.totalPrice.earnPoints - creditsApplied) * 100) / 100
        : 0,
    [cartQueryResult.data, creditsApplied]
  )

  const totalPointsBalance = meQueryResult.data?.me?.totalPointsBalance
  const totalPointsBalanceAvailable = meQueryResult.data?.me?.totalPointsBalanceAvailable
  const needSpend = Math.round(
    firstRewardProgram
      ? +totalPointsBalance > firstRewardProgram.node.rpThreshold
        ? 0
        : firstRewardProgram.node.rpThreshold - +totalPointsBalance
      : 0
  )
  const progress = firstRewardProgram ? (+totalPointsBalanceAvailable * 100) / firstRewardProgram?.node.rpThreshold : 0
  const currency = cartQueryResult.data?.cart?.totalPrice.currency || 'USD'

  const [currentCurrency, rate] = useMemo(() => {
    if (!currencyExchangeRateQueryResult.data?.currentCurrencyExchangeRate) return ['USD', 1]
    return [
      currencyExchangeRateQueryResult.data.currentCurrencyExchangeRate.currencyCode,
      currencyExchangeRateQueryResult.data.currentCurrencyExchangeRate.rate,
    ]
  }, [currencyExchangeRateQueryResult.data])
  return (
    <>
      {!!cartQueryResult.data?.cart?.count && (
        <div
          className='bg-white shadow-card order-2 md:order-1 sticky rounded-md overflow-hidden z-10'
          style={{ top: '120px' }}
        >
          <p className='text-bold text-lg p-16 mb-0'>{t('estimatedTotal', 'Estimated Total')}</p>
          <Divider sx={{ borderColor: '#f0f0f0' }} />
          <div className='p-16'>
            {!!creditsApplied && (
              <div className='flex items-center justify-between text-base mb-8'>
                <div>{t('creditsApplied', 'Credits applied')}</div>
                <span className={loading ? 'opacity-0' : ''}>
                  <FormattedPrice currency={currency} amount={+creditsApplied} />
                </span>
              </div>
            )}
            <div className='relative'>
              <Loader
                className='spinner spinner--xs'
                type='ThreeDots'
                color='#333'
                visible={loading}
                height={10}
                width={20}
              />
              <span className={`text-2xl font-bold ${loading ? 'opacity-0' : ''}`}>
                <FormattedPrice currency={currency} amount={itemSubtotal - creditsApplied} />
              </span>
            </div>
            <div className='text-sm mt-4'>
              {t('shippingAndTaxCalculatedInCheckout', 'Shipping cost and Tax calculated in Checkout')}
            </div>
          </div>
          <div className='text-gray text-sm flex justify-center mb-10'>
            <AfterPayInfo
              minPrice={itemSubtotal - creditsApplied}
              maxPrice={itemSubtotal - creditsApplied}
              currency={currency}
            />
          </div>
          <div className='px-16 pb-16 hidden md:block'>
            <Button
              component={Link}
              to={CHECKOUT_URL}
              color='secondary'
              variant='contained'
              size='large'
              disableElevation
              fullWidth
            >
              <span className='text-base tracking-1 font-bold'>{t('button.checkoutNow', 'Checkout now')}</span>
            </Button>
          </div>
        </div>
      )}
      <div className='mx-12 sm:mx-0 mb-24 md:mb-0 md:mt-24 bg-white shadow-card order-1 md:order-2 rounded-md overflow-hidden'>
        <CartShippingDiscount />
      </div>
      <div className='mx-12 sm:mx-0 mb-24 md:mb-0 md:mt-24 bg-white shadow-card order-1 md:order-2 rounded-md overflow-hidden'>
        {earnPoints > 0 && (
          <div>
            <div className='px-12 py-5 bg-secondary flex justify-between text-white text-11'>
              <h2 className='text-11 uppercase leading-20'>{t('rewards', 'Rewards')}</h2>
              <span>
                <Trans
                  i18nKey='asOfDate'
                  defaults={`as of <date/>`}
                  components={{ date: <FormattedDate value={new Date().toISOString()} /> }}
                />
              </span>
            </div>
            {isAuthenticated && firstRewardProgram && (
              <>
                <p className='text-center p-12 text-gray'>
                  <Trans
                    i18nKey='onlyPointsUntilNextRewardFormatted'
                    defaults={`Only <strong>{{needSpend}}</strong> until your next <strong><price/> Reward</strong>`}
                    values={{
                      needSpend,
                      rdThreshold: (firstRewardProgram.node.rdThreshold * rate).toFixed(0),
                    }}
                    components={{
                      strong: <strong className='text-black' />,
                      price: (
                        <FormattedPrice
                          currency={currentCurrency}
                          amount={firstRewardProgram.node.rdThreshold * rate}
                        />
                      ),
                    }}
                  />
                </p>
                <div className='relative mx-12 h-20 bg-gray-200 text-13'>
                  <div
                    className='h-full bg-secondary transition-width ease-in-out duration-1000 shadow-lg'
                    style={{
                      width: `${progress}%`,
                    }}
                  />
                </div>
                <div className='flex items-center justify-between leading-24 px-12 pb-12'>
                  <span>{totalPointsBalanceAvailable}</span>
                  <span>{firstRewardProgram.node.rpThreshold}</span>
                </div>
                <Divider />
              </>
            )}
            <div className='p-12'>
              <div className='flex items-center text-13'>
                <div className='w-40 mr-10'>
                  <img src={PointsSvg} alt='Points' className='max-w-full' />
                </div>
                <div className='flex-1'>
                  {!isAuthenticated && (
                    <span className='text-gray-800'>
                      <Trans
                        i18nKey='signInToEarnPoints'
                        defaults='<a>Sign in</a> to earn <strong>{{earnPoints}}</strong> points with this purchase!'
                        values={{ earnPoints: earnPoints }}
                        count={earnPoints}
                        components={{
                          a: (
                            <Link
                              className='text-blue no-underline'
                              to={ACCOUNT_CREATE_URL}
                              onClick={e => {
                                e.preventDefault()
                                handleSignIn()
                              }}
                            />
                          ),
                          strong: <strong className='text-black' />,
                        }}
                      />
                    </span>
                  )}
                  {isAuthenticated && (
                    <span className='text-gray-800'>
                      <Trans
                        i18nKey='youEarnPointsWithPurchase'
                        defaults={`Good news! You'll earn <strong><loader/>{{earnPoints}}</strong> points with this purchase!`}
                        values={{ earnPoints: earnPoints }}
                        count={earnPoints}
                        components={{
                          strong: <strong className={`relative text-black ${loading ? ' opacity-0' : ''}`} />,
                          loader: (
                            <Loader
                              className='spinner spinner--xs'
                              type='ThreeDots'
                              color='#333'
                              visible={loading}
                              height={100}
                              width={100}
                            />
                          ),
                        }}
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {!!cartQueryResult.data?.cart?.count && (
        <div
          className='fixed block md:hidden bottom-0 bg-white z-20 w-full p-12 left-0 right-0'
          style={{
            boxShadow: '0 -2px 4px 0 rgb(0 0 0 / 10%), 0 0 20px 0 rgb(0 0 0 / 10%)',
          }}
        >
          <div className='font-bold text-center text-base'>
            <span>Estimated Total: </span>{' '}
            <span className='relative'>
              <Loader
                className='spinner spinner--xs'
                type='ThreeDots'
                color='#333'
                visible={loading}
                height={100}
                width={100}
              />
              <span className={loading ? 'opacity-0' : ''}>
                <FormattedPrice currency={currency} amount={itemSubtotal - creditsApplied} />
              </span>
            </span>
          </div>
          <div className='text-gray text-sm flex justify-center mt-5'>
            <AfterPayInfo
              minPrice={itemSubtotal - creditsApplied}
              maxPrice={itemSubtotal - creditsApplied}
              currency={currency}
            />
          </div>
          <div className='pt-12'>
            <Button
              component={Link}
              to={CHECKOUT_URL}
              color='secondary'
              variant='contained'
              size='large'
              fullWidth
              disableElevation
            >
              <span className='text-base tracking-1 font-bold'>{t('button.checkoutNow', 'Checkout now')}</span>
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

export default CartLinesTotal
